import { Grid } from '@mui/material';
import { get, set } from 'lodash';
import { type Path } from 'react-hook-form';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import { IGroupHeaderConfig } from 'src/interfaces';
import { lang } from 'src/utils';

import { CategoryItemTitleProps } from './CategoryItemTitle.interface';

export const CategoryItemTitle = (props: CategoryItemTitleProps) => {
  const pillVisible = get(props.value, 'pill.visible', true);

  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  const handleFieldChange =
    (field: Path<CategoryItemTitleProps['value']>) => (value: any) => {
      props.handleTitleChange(
        set({ ...props.value } as IGroupHeaderConfig, field, value),
      );
    };

  const getBorderColor = (value?: string) => {
    return value?.split(/[, ]+/).pop();
  };

  return (
    <>
      <Grid item xs={12}>
        <SwitchButtonWidget
          value={props.value?.visible}
          label={lang.subheader}
          onChange={handleFieldChange('visible')}
        />
      </Grid>
      {props.value?.visible && (
        <>
          <Grid item xs={12}>
            <InputWidget
              label={lang.subheaderText}
              value={props.value?.text}
              onChange={handleFieldChange('text')}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectDropdownWidget
              selectOptions={mappedFonts}
              value={
                props?.value?.font ||
                props?.subHeaderComponentConfig?.font ||
                props.theme?.config.subheader?.font
              }
              label={lang.fontFamily}
              onChange={handleFieldChange('font')}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={
                props.value?.fontSize ??
                props.subHeaderComponentConfig?.fontSize ??
                props.theme?.config.subheader?.fontSize
              }
              onChange={handleFieldChange('fontSize')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.fontColor}
              value={
                props.value?.color ??
                props.subHeaderComponentConfig?.color ??
                props.theme?.config.subheader?.color
              }
              onChange={handleFieldChange('color')}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={
                props.value?.lineHeight ??
                props.subHeaderComponentConfig?.lineHeight ??
                props.theme?.config.subheader?.lineHeight
              }
              onChange={handleFieldChange('lineHeight')}
              options={{ suffix: 'px' }}
            />
          </Grid>

          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.marginBelow}
              value={
                props.value?.marginBottom ??
                props.subHeaderComponentConfig?.marginBottom
              }
              onChange={handleFieldChange('marginBottom')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={
                props?.value?.height ??
                props.subHeaderComponentConfig?.height ??
                props.theme?.config.subheader?.height
              }
              onChange={handleFieldChange('height')}
              options={{ suffix: 'px' }}
            />
          </Grid>

          <Grid item xs={12}>
            <SwitchButtonWidget
              value={pillVisible}
              label={lang.pill}
              onChange={handleFieldChange('pill.visible')}
            />
          </Grid>

          {pillVisible && (
            <>
              <Grid item xs={6}>
                <ColorPickerWidget
                  label={lang.backgroundColor}
                  value={
                    props.value?.backgroundColor ??
                    props.subHeaderComponentConfig?.backgroundColor ??
                    props.theme?.config.subheader?.backgroundColor
                  }
                  onChange={handleFieldChange('backgroundColor')}
                  options={{
                    hideAIcon: true,
                    backgroundColor: true,
                    border: true,
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <ColorPickerWidget
                  label={lang.borderColor}
                  value={getBorderColor(
                    props.value?.border ||
                      props.subHeaderComponentConfig?.border ||
                      props.theme?.config?.subheader?.border,
                  )}
                  onChange={(value) =>
                    handleFieldChange('border')(`2px solid ${value}`)
                  }
                  options={{
                    hideAIcon: true,
                    backgroundColor: true,
                    border: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputWidget
                  label={lang.borderRadius}
                  value={
                    props?.value?.borderRadius ||
                    props.subHeaderComponentConfig?.borderRadius ||
                    props.theme?.config.subheader?.borderRadius
                  }
                  onChange={handleFieldChange('borderRadius')}
                  options={{ suffix: 'px' }}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
};
